import styled from '@emotion/styled';
import { FocusEvent, ReactNode, useEffect, useRef } from 'react';

import { Tabs } from '@/design-system';
import { forMobile } from '@/design-system/responsive';

export type MobilePortfolioSectionKey = 'assets' | 'pending' | 'crypto' | 'ownership';

export interface MobilePortfolioSection {
  value: MobilePortfolioSectionKey;
  label: string;
  content: ReactNode;
}

interface Availability {
  isCryptoWalletEnabled: boolean;
  isInvestingFlowEnabled: boolean;
  isPersonalUser: boolean;
}

export const getMobilePortfolioSectionKeys = ({
  isCryptoWalletEnabled,
  isInvestingFlowEnabled,
  isPersonalUser,
}: Availability): MobilePortfolioSectionKey[] => [
  'assets',
  ...(isInvestingFlowEnabled ? (['pending'] as const) : []),
  ...(isCryptoWalletEnabled ? (['crypto'] as const) : []),
  ...(isPersonalUser ? (['ownership'] as const) : []),
];

const SectionRoot = styled(Tabs.Root)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[24],
  minWidth: 0,
}));

const TabScroller = styled('div')({
  width: '100%',
  overflowX: 'auto',
  scrollbarWidth: 'none',
  WebkitOverflowScrolling: 'touch',

  '&::-webkit-scrollbar': { display: 'none' },
});

const SectionList = styled(Tabs.List)({
  minWidth: 'max-content',

  [forMobile]: {
    '& [role="tab"]': { minHeight: 44 },
  },
});

interface MobilePortfolioSectionTabsProps {
  sections: MobilePortfolioSection[];
  value: MobilePortfolioSectionKey;
  onValueChange: (_value: MobilePortfolioSectionKey) => void;
}

export const MobilePortfolioSectionTabs = ({
  sections,
  value,
  onValueChange,
}: MobilePortfolioSectionTabsProps) => {
  const requestedFallbackRef = useRef<MobilePortfolioSectionKey | undefined>(undefined);
  const effectiveValue = sections.some((section) => section.value === value)
    ? value
    : sections[0]?.value;

  useEffect(() => {
    if (effectiveValue === value) {
      requestedFallbackRef.current = undefined;
      return;
    }

    if (effectiveValue && requestedFallbackRef.current !== effectiveValue) {
      requestedFallbackRef.current = effectiveValue;
      onValueChange(effectiveValue);
    }
  }, [effectiveValue, onValueChange, value]);

  if (sections.length === 0) return null;

  const handleTabFocus = (event: FocusEvent<HTMLDivElement>) => {
    const target = event.target;
    if (!(target instanceof HTMLElement) || target.getAttribute('role') !== 'tab') return;

    target.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });

    const tabs = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('[role="tab"]'));
    const nextValue = sections[tabs.indexOf(target)]?.value;
    if (nextValue && nextValue !== effectiveValue) onValueChange(nextValue);
  };

  return (
    <SectionRoot
      value={effectiveValue}
      onValueChange={(next) => onValueChange(next as MobilePortfolioSectionKey)}
      variant="bordered"
      size="lg"
    >
      <TabScroller onFocusCapture={handleTabFocus}>
        <SectionList>
          {sections.map((section) => (
            <Tabs.Tab key={section.value} value={section.value} label={section.label} />
          ))}
        </SectionList>
      </TabScroller>

      {sections.map((section) => (
        <Tabs.Panel key={section.value} value={section.value}>
          {section.content}
        </Tabs.Panel>
      ))}
    </SectionRoot>
  );
};
